<template>
    <v-container v-if="event">
        <v-row>
            <v-col cols="12" order-md="2">
                <div class="d-flex justify-center justify-md-end">
                    <action-buttons
                        :state="editState"
                        :permissions="userPermission"
                        :disabled="isLoading"
                        @save="handleSave"
                        @cancel="handleCancel"
                        @close="handleClose"
                        :is-dirty="isDirty"
                        class="d-flex justify-end"
                    />
                </div>
            </v-col>
        </v-row>
        <v-form ref="form" v-model="valid">
            <v-row>
                <v-col cols="12" md="4">
                    <v-text-field
                        v-model="event.name"
                        label="Event Name"
                        :error-messages="validation.nameMessage"
                        @change="validate"
                    />
                </v-col>
                <v-col cols="12" md="8">
                    <v-menu
                        ref="menu"
                        v-model="startPicker"
                        :close-on-content-click="false"
                        :return-value.sync="tempDateRange"
                        transition="scale-transition"
                        offset-y
                        min-width="auto"
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <v-row>
                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="fromDate"
                                        label="From"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        :error-messages="validation.datesMessage"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" md="4">
                                    <v-text-field
                                        v-model="toDate"
                                        label="To"
                                        prepend-icon="mdi-calendar"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                    ></v-text-field>
                                </v-col>
                            </v-row>
                        </template>
                        <v-date-picker
                            v-model="tempDateRange"
                            range
                            scrollable
                            show-adjacent-months
                            label="Event Dates"
                        >
                            <v-spacer></v-spacer>
                            <v-btn text color="primary" @click="startPicker = false">
                                Cancel
                            </v-btn>
                            <v-btn text color="primary" @click="saveDateChange">
                                OK
                            </v-btn>
                        </v-date-picker>
                    </v-menu>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-textarea v-model="event.location" label="Location" rows="3"></v-textarea>
                </v-col>
                <v-col cols="12" md="4">
                    <v-row>
                        <v-col cols="12">
                            <v-checkbox v-model="event.isRumourEvent" label="Is this a Rumour Event"></v-checkbox>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <v-select
                                v-model="event.leadTeamId"
                                :items="teams"
                                label="Default Team"
                                item-text="name"
                                item-value="id"
                                outlined
                                dense
                            ></v-select>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
            <v-row v-for="(day, i) in days" :key="i">
                <v-col cols="3">{{ day.date | luxonShortDayDate }}</v-col>
                <v-col cols="3">
                    <time-selector v-model="day.startMinutes" label="Time In" @change="timeChanged"></time-selector>
                </v-col>
                <v-col cols="3">
                    <time-selector v-model="day.endMinutes" label="Time Out" @change="timeChanged"></time-selector>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>
<script>
import api from '@/api'
import { mapState } from 'vuex'
import navGuardConfirmMixin from '../../mixins/navGuardConfirmMixin'
import editorMixin from '../../mixins/editorMixin'
import { DateTime } from 'luxon'
import TimeSelector from '@/components/TimeSelector'
export default {
    name: 'EventEditor',
    mixins: [navGuardConfirmMixin, editorMixin],
    components: {
        'time-selector': TimeSelector,
    },
    data() {
        return {
            valid: true,
            event: null,
            startPicker: false,
            dateRange: [],
            tempDateRange: [],
            days: [],
            startDate: '',
            endDate: '',
            defaultTeamId: 0,
            teams: [],
            validation: {
                nameMessage: '',
                datesMessage: '',
            },
        }
    },
    props: {
        id: {
            type: [String, Number],
            required: false,
            default: undefined,
        },
    },
    watch: {
        event: {
            deep: true,
            handler() {
                this.isDirty = true
            },
        },
        days: {
            deep: true,
            handler() {
                this.isDirty = true
            },
        },
        startPicker() {
            this.tempDateRange = this.dateRange
        },
    },
    computed: {
        ...mapState(['user']),
        userPermission() {
            if (this.user && this.user.editEvents) {
                return this.user.editEvents.all
            } else {
                return 0
            }
        },
        editState() {
            return this.id ? 'Edit' : 'Create'
        },
        fromDate() {
            return this.dateRange.length > 0 ? this.dateRange[0] : ''
        },
        toDate() {
            return this.dateRange.length > 1 ? this.dateRange[1] : ''
        },
        timeInEvents() {
            return this.event.days.map((x, i) => {
                return {
                    name: `Time In - Day ${i + 1}`,
                    start: this.getTimeStamp(x.date, x.timeInStartMinutes),
                    end: this.getTimeStamp(x.date, x.timeInStartMinutes, x.timeInDurationMinutes),
                }
            })
        },
    },
    mounted() {
        this.getTeams()
        if (this.id) {
            this.getEvent()
        } else {
            this.newEvent()
        }
    },
    methods: {
        getEvent() {
            this.isLoading = true
            api.get(`event/Get/${this.id}`)
                .then(res => {
                    this.event = res
                    this.setDatesAndDays()
                })
                .finally(() => {
                    this.isDirty = false
                    this.isLoading = false
                })
        },
        newEvent() {
            this.event = {}
        },
        saveDateChange() {
            this.startPicker = false
            let start = DateTime.fromISO(this.tempDateRange[0])
            let end = DateTime.fromISO(this.tempDateRange[1])
            if (start > end) {
                this.dateRange = this.tempDateRange.reverse()
            } else {
                this.dateRange = this.tempDateRange
            }
            this.eventDays()
        },
        setDatesAndDays() {
            this.dateRange = []
            if (this.isValidDate(this.event.startDate)) {
                this.dateRange.push(DateTime.fromISO(this.event.startDate).toISODate())
            }
            if (this.isValidDate(this.event.endDate)) {
                this.dateRange.push(DateTime.fromISO(this.event.endDate).toISODate())
            }
            this.days = this.event.days.map(x => {
                x.startMinutes = x.timeInStartMinutes
                x.endMinutes = this.calculateEndTime(x.timeInStartMinutes, x.timeInDurationMinutes)
                return x
            })
        },
        calculateEndTime(startTime, durationMinutes) {
            let endTime = startTime + durationMinutes
            //1440 = 24 * 60
            if (endTime >= 1440) {
                endTime = endTime - 1440
            }
            return endTime
        },
        eventDays() {
            if (this.dateRange.length < 2) return
            let start = DateTime.fromISO(this.dateRange[0])
            let end = DateTime.fromISO(this.dateRange[1])
            this.event.startDate = start.toISODate()
            this.event.endDate = end.toISODate()
            let daysDiff = end.diff(start, 'days').toObject().days
            for (let i = 0; i <= daysDiff; i++) {
                if (this.days.length > i) {
                    this.days[i].date = start.toISODate()
                } else {
                    this.days.push({
                        date: start.toISODate(),
                        startMinutes: i == 0 ? 1020 : 600,
                        endMinutes: 60,
                    })
                }
                start = start.plus({ days: 1 })
            }
            if (this.days.length > daysDiff + 1) {
                this.days = this.days.slice(0, daysDiff + 1)
            }
            this.isDirty = true
        },
        getTeams() {
            this.isLoading = true
            api.getTeams()
                .then(res => {
                    this.teams = Object.freeze(res)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        handleSave() {
            if (!this.validate()) return
            this.isSaving = true
            let data = Object.assign({}, this.event)
            data.days = this.days
            let endpoint = this.id ? 'update' : 'create'
            api.post(`event/${endpoint}`, data)
                .then(() => {
                    this.showSnackbar('success', `Event ${endpoint}d`)
                    this.isDirty = false
                })
                .finally(() => {
                    this.isSaving = false
                })
        },
        isValidDate(value) {
            if (!value || value === 'Invalid DateTime' || (DateTime.isDateTime(value) && !value.isValid)) {
                return false
            }
            return true
        },
        getTimeStamp(date, time, duration = 0) {
            if (!this.isValidDate(date)) return
            let dateTime = DateTime.fromISO(date, { zone: 'utc' }).plus({ minutes: time + duration })
            return `${dateTime.toISODate()} ${dateTime.toLocaleString(DateTime.TIME_24_SIMPLE)}`
        },
        intervalFormat(interval) {
            return interval.time
        },
        handleCancel() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        handleClose() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        validate() {
            if (!this.event.name || !this.event.name.trim().length > 0) {
                this.validation.nameMessage = 'Name is required'
                return false
            }
            if (this.dateRange.length != 2) {
                this.validation.datesMessage = 'The Date Range of the event is required'
                return false
            }
            let start = DateTime.fromISO(this.dateRange[0])
            let end = DateTime.fromISO(this.dateRange[1])
            if (end < start) {
                this.validate.datesMessage = 'Start Date must be before End Date'
                return false
            }
            return true
        },
        timeChanged() {
            this.isDirty = true
        },
    },
}
</script>
